import { useMemo, useState } from 'react'
import { Box, Button, Grid, IconProps, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Section } from '../section'
import { icons } from '../../../../../../utils/icons'
import { useTranslation } from 'react-i18next'
import ActionCard from '../actionCard'
import dayjs from 'dayjs'
import { Constants } from '../../../../../../utils/constants'
import { ActionPlanAnswer } from '../../../../../../service/actionPlans/actionPlanById'
import { Evidence } from '../../../../../../service/reportFolio/response'
import { useEvidences } from '../../../../../../hooks/evidences/useEvidences'
import { useEvidenceDownload } from '../../../../../calendar/pages/detail/hooks'
import EvidenceDetail from '../evidenceDetail'
import { GoBack } from '../../../../../../components/goBack'
import { SnackSimple } from '../../../../../../components'
import { VariantCard } from '../actionCard/enum'
import { PlanResponsesSectionProps } from './type'
import { useStyles } from './styles'
import { NoResponse } from '@/components/errors/noResponse'
import { Status } from '../../../../../../utils/enums'

function PlanResponsesSection({
  answerActionPlan,
  aproveDisable,
  showActions,
  onApprove,
  onReject,
}: PlanResponsesSectionProps) {
  const { t } = useTranslation()
  const styles = useStyles()
  const [selectedAnswer, setSelectedAnswer] = useState<ActionPlanAnswer>()
  const answerLength = answerActionPlan?.length ?? 0

  const reviewStyle = useMemo(() => {
    if (selectedAnswer?.statusId === Status.APPROVED) {
      return { variantCard: VariantCard.SUCCESS, labelKey: 'actionPlan.aproveBy' }
    }
    if (selectedAnswer?.statusId === Status.REJECTED) {
      return { variantCard: VariantCard.ERROR, labelKey: 'actionPlan.rejectBy' }
    }
    return null
  }, [selectedAnswer?.statusId])

  const { evidences, existEvidences } = useEvidences({
    evidencesResponse: selectedAnswer?.evidencesAnswer,
  })

  const {
    disable,
    openSnackbar,
    handleCloseSnackbar,
    downloadEvidences,
  } = useEvidenceDownload(
    'evidencias de detalle de respuesta',
    (selectedAnswer?.evidencesAnswer as Evidence[]) ?? []
  )

  const handleSelect = (actionPlan: ActionPlanAnswer) => {
    setSelectedAnswer(actionPlan)
  }

  const handleBack = () => {
    setSelectedAnswer(undefined)
  }

  const renderCommentBlock = (title: string, text?: string | null) => {
    if (!text) return null

    return (
      <Box sx={styles.commentBlock}>
        <Typography sx={styles.commentTitle}>{title}</Typography>
        <Typography sx={styles.commentText}>{text}</Typography>
      </Box>
    )
  }

  const renderList = () => (
    <Box sx={styles.listContainer}>
      {!!answerLength && answerActionPlan?.map((actionPlan) => {
        const isSelected = selectedAnswer?.id === actionPlan.id

        return (
          <ActionCard
            key={actionPlan.id}
            hover
            onClick={() => handleSelect(actionPlan)}
            sx={styles.card}
            icon={{ position: 'start', icon: <ActionCard.Icon<IconProps> color='primary_company' icon={icons('person')} /> }}
          >
            <ActionCard.Header label={t('actionPlan.emitBy')} />

            <ActionCard.Title label={`${actionPlan?.userResponse?.firstName ?? ''} ${actionPlan?.userResponse?.lastName ?? ''}`.trim()} />

            <ActionCard.Footer label={dayjs(actionPlan.create_time).locale('es').format(Constants.dayjsHumanFormat)} />
          </ActionCard>
        )
      })}

      {!answerLength && (
        <NoResponse label={t('actionPlan.noActionPlan')} />
      )}
    </Box>
  )

  const renderDetail = () => {
    if (!selectedAnswer) return null

    return (
      <Box sx={styles.detailContainer}>
        <Typography sx={styles.detailTitle}>{t('actionPlan.evidenceList')}</Typography>

        <EvidenceDetail
          existEvidences={existEvidences}
          evidences={{
            image: evidences?.images,
            comment: evidences?.comments,
            documents: evidences?.documents,
            video: evidences?.video,
            sign: evidences?.sign,
          }}
        />

        {existEvidences && (
          <Button
            sx={styles.downloadButton}
            variant='text_primary'
            onClick={downloadEvidences}
            disabled={disable}
          >
            {t('schedule.buttons.downloadEvidence')}
          </Button>
        )}

        <Box sx={styles.actionsWrapper}>
          {showActions && (
            <Box sx={styles.actionsContainer}>
              <Button variant="container_transparent" onClick={onReject}>
                {t('general.reject')}
              </Button>
              <LoadingButton disabled={aproveDisable} variant="primary_c" onClick={onApprove} loading={aproveDisable}>
                {t('general.aprove')}
              </LoadingButton>
            </Box>
          )}
        </Box>

        <SnackSimple
          close={() => handleCloseSnackbar(false)}
          open={openSnackbar.open}
          type={openSnackbar.type as any}
          message={openSnackbar.message}
        />
      </Box>
    )
  }

  return (
    <Section
      icon={<Box sx={styles.countBadge}>{answerLength}</Box>}
      title={t('actionPlan.actionPlanResponse')}
    >
      {selectedAnswer ? (
        <Grid container spacing={3} sx={styles.detailGrid}>
          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            <Box sx={styles.leftColumn}>
              <GoBack
                color="primary"
                arrowProps={{ onClick: handleBack }}
                title={t('actionPlan.responseDetail')}
              />

              {!!selectedAnswer.userReview && reviewStyle && (
                <ActionCard
                  variantCard={reviewStyle.variantCard}
                  icon={{ position: 'start', icon: <ActionCard.Icon<IconProps> color='primary_company' icon={icons('person')} /> }}
                >
                  <ActionCard.Header label={t(reviewStyle.labelKey)} />

                  <ActionCard.Title label={`${selectedAnswer.userReview.firstName ?? ''} ${selectedAnswer.userReview.lastName ?? ''}`.trim()} />

                  <ActionCard.Footer label={dayjs(selectedAnswer.update_time).locale('es').format(Constants.dayjsHumanFormat)} />
                </ActionCard>
              )}

              <ActionCard
                icon={{ position: 'start', icon: <ActionCard.Icon<IconProps> color='primary_company' icon={icons('person')} /> }}
              >
                <ActionCard.Header label={t('actionPlan.emitBy')} />

                <ActionCard.Title label={`${selectedAnswer.userResponse?.firstName ?? ''} ${selectedAnswer.userResponse?.lastName ?? ''}`.trim()} />

                <ActionCard.Footer label={dayjs(selectedAnswer.create_time).locale('es').format(Constants.dayjsHumanFormat)} />
              </ActionCard>

              {renderCommentBlock(t('general.comment'), selectedAnswer.comment)}
              {renderCommentBlock(t('actionPlan.rejectMotive'), selectedAnswer.reasonRejection)}
            </Box>
          </Grid>

          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            {renderDetail()}
          </Grid>
        </Grid>
      ) : (
        renderList()
      )}
    </Section>
  )
}

export { PlanResponsesSection }
